/**
 * @author       jordan.sookikian@zumper.com (Jordan Sookikian)
 * @copyright Copyright (c) 2021, Zumper
 */

import Listable from '@zcode/nr/es/shared/bizos/listable'
import {
  type ListingIdAndBuildingIdTuple,
  selectStreamChannel,
  selectStreamChannelId,
  selectStreamChannelIdKey,
} from '@zcode/nr/es/shared/modules/stream/selectors'
import type { State } from '@zcode/state-type'
import { ONE_HOUR } from '@zcode/utils'
import { compose } from 'redux'
import type { BuildingListingIdParams } from '../types'

export { selectStreamChannelId, selectStreamChannelIdKey }

export const selectStream = (state: State) => state?.stream

export const selectUnreadCounts = compose((stream) => stream?.unreadCounts, selectStream)

export const selectRenterUnreadCount = compose(
  (unreadCounts) => unreadCounts?.renterUnreadCount ?? 0,
  selectUnreadCounts
)

export const selectStreamChannelIdBuildingOnlyKey = (
  ...[listingId, buildingId]: ListingIdAndBuildingIdTuple
) => {
  if (listingId && buildingId) {
    return selectStreamChannelIdKey(undefined, buildingId)
  }
}

export const listingAndBuildingIdToTuple = (
  props: BuildingListingIdParams
): ListingIdAndBuildingIdTuple => {
  let { buildingId, listingId } = props ?? {}
  if (props instanceof Listable && buildingId) {
    // a Listable that's a building will have a listing_id equal to one of its
    // floor plans' ids. That can prevent us from finding a matching conversation for the
    // building, so ignore it
    listingId = null
  }
  return [listingId, buildingId]
}

export const selectStreamChannelFetching = (state: State, ...args: ListingIdAndBuildingIdTuple) =>
  selectStreamChannel(state, ...args)?.fetching

export const selectFetchedAllStreamChannels = compose(
  (stream) => stream.fetchedAllChannels,
  selectStream
)

export const selectFetchedUnfilteredChannelsAt = compose(
  (stream) => stream.fetchedUnfilteredChannelsAt,
  selectStream
)

export const selectFetchedUnfilteredChannelsRecently = compose(
  (timestamp) => timestamp != null && Date.now() - timestamp < ONE_HOUR,
  selectFetchedUnfilteredChannelsAt
)

export const selectFetchingUnfilteredChannels = compose(
  (stream) => stream.fetchingUnfilteredChannels,
  selectStream
)

export const selectStreamChannelOrUnfilteredFetching = (
  state: State,
  ...ids: ListingIdAndBuildingIdTuple
) => {
  return selectFetchingUnfilteredChannels(state) || selectStreamChannelFetching(state, ...ids)
}
